export type CertificatePage = {
  slug: string;
  title: string;
  subtitle: string;
  summary: string;
  bullets: string[];
  accent: string;
};

export const certificatePages: CertificatePage[] = [
  {
    slug: '9001-2015',
    title: 'ISO 9001:2015',
    subtitle: 'Quality Management System',
    summary:
      'ISO 9001 demonstrates a commitment to consistent quality, process control, and customer satisfaction.',
    bullets: [
      'Quality management system certification',
      'Improves process consistency and customer confidence',
      'Widely recognized across all industries',
    ],
    accent: '#0f766e',
  },
  {
    slug: '13485-2016',
    title: 'ISO 13485:2016',
    subtitle: 'Medical Device Quality Management',
    summary:
      'ISO 13485 supports medical device organizations with documented quality controls and regulatory readiness.',
    bullets: [
      'Medical device quality management system',
      'Supports product safety and regulatory documentation',
      'Useful for manufacturers, suppliers, and service providers',
    ],
    accent: '#2563eb',
  },
  {
    slug: '8000-2014',
    title: 'ISO 8000:2014',
    subtitle: 'Data Quality Management',
    summary:
      'ISO 8000 helps organizations improve the quality, accuracy, and governance of enterprise data.',
    bullets: [
      'Improves trusted business records',
      'Supports data governance and reporting discipline',
      'Helpful for process-heavy and data-led organizations',
    ],
    accent: '#0891b2',
  },
  {
    slug: 'gmp-compliance',
    title: 'GMP Compliance',
    subtitle: 'Good Manufacturing Practice',
    summary:
      'GMP compliance helps organizations establish hygienic, controlled, and repeatable manufacturing processes.',
    bullets: [
      'Production and quality-control documentation',
      'Supports audit preparation and process discipline',
      'Relevant for food, pharma, cosmetics, and related sectors',
    ],
    accent: '#16a34a',
  },
  {
    slug: '45001-2018',
    title: 'ISO 45001:2018',
    subtitle: 'Occupational Health and Safety',
    summary:
      'ISO 45001 supports safer workplaces by identifying and reducing occupational health and safety risks.',
    bullets: [
      'Health and safety management',
      'Reduces risk and incident exposure',
      'Improves employee wellbeing',
    ],
    accent: '#b45309',
  },
  {
    slug: '50000-2011',
    title: 'ISO 50000:2011',
    subtitle: 'Energy Management',
    summary:
      'ISO 50000:2011 supports structured energy planning, performance tracking, and resource efficiency.',
    bullets: [
      'Energy performance improvement framework',
      'Helps reduce consumption and operating costs',
      'Supports measurable conservation practices',
    ],
    accent: '#0d9488',
  },
  {
    slug: 'ce-marking',
    title: 'CE Marking',
    subtitle: 'Product Conformity Support',
    summary:
      'CE Marking support helps product businesses prepare conformity evidence for applicable European requirements.',
    bullets: [
      'Product documentation and conformity pathway support',
      'Helps demonstrate market readiness',
      'Useful for regulated product manufacturers and exporters',
    ],
    accent: '#1d4ed8',
  },
  {
    slug: '14001-2015',
    title: 'ISO 14001:2015',
    subtitle: 'Environmental Management System',
    summary:
      'ISO 14001 helps organizations manage environmental responsibilities in a structured and measurable way.',
    bullets: [
      'Environmental management system certification',
      'Supports sustainability and compliance goals',
      'Suitable for manufacturing, services, and public sector organizations',
    ],
    accent: '#15803d',
  },
  {
    slug: 'kosher-compliance',
    title: 'Kosher Compliance',
    subtitle: 'Kosher Process Controls',
    summary:
      'Kosher compliance support helps food and product businesses validate process controls for kosher requirements.',
    bullets: [
      'Process and ingredient review support',
      'Supports food and product line credibility',
      'Helpful for domestic and export market access',
    ],
    accent: '#7c3aed',
  },
  {
    slug: 'bifma-compliance',
    title: 'BIFMA Compliance',
    subtitle: 'Furniture Standards Support',
    summary:
      'BIFMA compliance support helps furniture businesses align products with recognized performance and safety standards.',
    bullets: [
      'Furniture product standard readiness',
      'Supports third-party certification preparation',
      'Useful for commercial and institutional furniture suppliers',
    ],
    accent: '#475569',
  },
  {
    slug: 'haccp',
    title: 'HACCP',
    subtitle: 'Food Safety Risk Control',
    summary:
      'HACCP provides a preventive food safety framework based on identifying and controlling critical hazards.',
    bullets: [
      'Critical control point planning',
      'Food safety documentation and process review',
      'Suitable for food production and handling operations',
    ],
    accent: '#dc2626',
  },
  {
    slug: 'fda-compliance',
    title: 'FDA Compliance',
    subtitle: 'Regulated Product Readiness',
    summary:
      'FDA compliance support helps regulated businesses organize quality evidence, records, and process controls.',
    bullets: [
      'Quality documentation and record support',
      'Helps prepare for regulated product expectations',
      'Relevant for food, medical, cosmetic, and related products',
    ],
    accent: '#be123c',
  },
  {
    slug: 'organic-compliance',
    title: 'Organic Compliance',
    subtitle: 'Organic Certification Support',
    summary:
      'Organic compliance support helps businesses demonstrate transparent production controls and label trust.',
    bullets: [
      'Organic process and documentation support',
      'Supports product validation and market trust',
      'Useful for agriculture, food, cosmetic, and export businesses',
    ],
    accent: '#65a30d',
  },
  {
    slug: '29990-2010',
    title: 'ISO 29990:2010',
    subtitle: 'Learning Services Management',
    summary:
      'ISO 29990 focuses on quality management for learning services and measurable training outcomes.',
    bullets: [
      'Learning service quality framework',
      'Supports consistent delivery and evaluation',
      'Useful for education, training, and development providers',
    ],
    accent: '#9333ea',
  },
  {
    slug: '22716-2007',
    title: 'ISO 22716:2007',
    subtitle: 'Cosmetics GMP',
    summary:
      'ISO 22716 gives cosmetic businesses good manufacturing practice guidance for quality, hygiene, and consistency.',
    bullets: [
      'Cosmetics manufacturing practice support',
      'Improves hygiene and process consistency',
      'Helpful for cosmetic manufacturers and suppliers',
    ],
    accent: '#db2777',
  },
  {
    slug: 'rohs-compliance',
    title: 'RoHS Compliance',
    subtitle: 'Restricted Substances Compliance',
    summary:
      'RoHS compliance support helps product businesses manage restricted substance requirements for electrical and electronic goods.',
    bullets: [
      'Restricted substance documentation support',
      'Supports product-level conformity evidence',
      'Useful for electronics manufacturers and exporters',
    ],
    accent: '#0284c7',
  },
  {
    slug: '22000-2018',
    title: 'ISO 22000:2018',
    subtitle: 'Food Safety Management System',
    summary:
      'ISO 22000 helps organizations manage food safety hazards across production, handling, and supply-chain activities.',
    bullets: [
      'Food safety management certification',
      'Supports end-to-end supply-chain control',
      'Useful for food producers, processors, and distributors',
    ],
    accent: '#ea580c',
  },
  {
    slug: '15189-2012',
    title: 'ISO 15189:2012',
    subtitle: 'Medical Laboratory Quality',
    summary:
      'ISO 15189 supports medical laboratories with quality and competence requirements for reliable testing outcomes.',
    bullets: [
      'Laboratory quality and competence framework',
      'Supports reliable testing processes',
      'Useful for diagnostic and medical laboratories',
    ],
    accent: '#0369a1',
  },
  {
    slug: '27001-2013',
    title: 'ISO 27001:2013',
    subtitle: 'Information Security Management System',
    summary:
      'ISO 27001 helps protect confidential data through risk-based controls and certified information security practices.',
    bullets: [
      'Information security management certification',
      'Supports risk-based controls and documentation',
      'Trusted for technology-driven and regulated businesses',
    ],
    accent: '#7c3aed',
  },
  {
    slug: '20000-1-2018',
    title: 'ISO 20000-1:2018',
    subtitle: 'IT Service Management',
    summary:
      'ISO 20000-1 helps organizations improve IT service governance, delivery quality, and customer value.',
    bullets: [
      'IT service management certification',
      'Improves service delivery and accountability',
      'Useful for IT teams, managed service providers, and support operations',
    ],
    accent: '#4338ca',
  },
  {
    slug: '15378-2017',
    title: 'ISO 15378:2017',
    subtitle: 'Primary Packaging for Medicinal Products',
    summary:
      'ISO 15378 integrates GMP expectations with quality requirements for primary packaging materials used in medicinal products.',
    bullets: [
      'Quality system for medicinal packaging materials',
      'Combines ISO quality practices with GMP expectations',
      'Useful for packaging manufacturers and pharma supply chains',
    ],
    accent: '#0f766e',
  },
];

export const offeringCards = [
  'ISO 9001:2015',
  'ISO 13485:2016',
  'ISO 8000:2014',
  'GMP Compliance',
  'ISO 45001:2018',
  'ISO 50000:2011',
  'CE Marking',
  'ISO 14001:2015',
  'Kosher Compliance',
  'BIFMA Compliance',
  'HACCP',
  'FDA Compliance',
  'Organic Compliance',
  'ISO 29990:2010',
  'ISO 22716:2007',
  'RoHS Compliance',
  'ISO 22000:2018',
  'ISO 15189:2012',
  'ISO 27001:2013',
  'ISO 20000-1:2018',
  'ISO 15378:2017',
];

export const getCertificateSlug = (title: string) =>
  certificatePages.find((page) => page.title === title)?.slug ?? '14001-2015';
